import { distinctUntilChanged, map } from 'rxjs';
import { Injectable } from '@angular/core';
import type { Params } from '@angular/router';
import { Store } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';
import { isEqual } from 'lodash';

@Injectable()
export class SlideViewerFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  themeColor$ = this.store.select(FlatStates.PageEntityState.themeColor$);
  currentSelectedSlideSidebarSize$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlideSidebarSize$);
  currentSelectedSlideMainFullSize$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlideMainFullSize$);
  currentSelectedSlideSidebarFullSize$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlideSidebarFullSize$);
  currentSelectedSlideMainSize$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlideMainSize$);

  currentSelectedSlideNodes$ = this.store.select(FlatStates.NodeEntityState.currentSelectedSlideNodes$);
  currentSelectedSlideNode$ = this.store.select(FlatStates.NodeEntityState.currentSelectedSlideNode$);

  currentSelectedSlideNodesFiltered$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlideNodesFiltered$);
  currentSelectedSlideIndex$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlideIndex$);
  currentSelectedSlidePercentage$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlidePercentage$);
  previousSlideNode$ = this.store.select(FlatStates.PageEntityState.previousSlideNode$);
  previousSlideUrl$ = this.store.select(FlatStates.PageEntityState.previousSlideUrl$);
  nextSlideNode$ = this.store.select(FlatStates.PageEntityState.nextSlideNode$);
  nextSlideUrl$ = this.store.select(FlatStates.PageEntityState.nextSlideUrl$);
  currentSelectedSlidePage$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlidePage$);
  currentSelectedSlideLayers$ = this.store.select(FlatStates.SlideLayerEntityState.currentSelectedSlideLayers$);
  currentSelectedSlideLayer$ = this.store.select(FlatStates.SlideLayerEntityState.currentSelectedSlideLayer$);
  currentSelectedSlideValidImages$ = this.store.select(FlatStates.ImageEntityState.currentSelectedSlideValidImages$)
  imageIndex$ = this.store.select(FlatStates.ImageEntityState.imageIndex$);
  currentSelectedSlideImages$ = this.store.select(FlatStates.ImageEntityState.currentSelectedSlideImages$);
  currentSelectedSlideImage$ = this.store.select(FlatStates.ImageEntityState.currentSelectedSlideImage$);
  currentSelectedSlideValidMedias$ = this.store.select(FlatStates.MediaEntityState.currentSelectedSlideValidMedias$);
  currentSelectedSlideMedia$ = this.store.select(FlatStates.MediaEntityState.currentSelectedSlideMedia$);
  currentSelectedSlideImageAreaGroups$ = this.store.select(FlatStates.AnnotationGroupEntityState.currentSelectedSlideImageAreaGroups$);
  currentSelectedSlideGroups$ = this.store.select(FlatStates.AnnotationGroupEntityState.currentSelectedSlideGroups$);
  currentSelectedSlideGroup$ = this.store.select(FlatStates.AnnotationGroupEntityState.currentSelectedSlideGroup$);
  currentSelectedSlideImageAreaGroup$ = this.store.select(FlatStates.AnnotationGroupEntityState.currentSelectedSlideImageAreaGroup$);
  currentSelectedImagePois$ = this.store.select(FlatStates.PointOfInterestEntityState.currentSelectedImagePois$);
  currentSelectedSlidePoi$ = this.store.select(FlatStates.PointOfInterestEntityState.currentSelectedSlidePoi$);
  currentSelectedImagePoi$ = this.store.select(FlatStates.PointOfInterestEntityState.currentSelectedSlidePoi$);
  currentSelectedSlideAnnotation$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideAnnotation$);

  allCurrentSelectedSlideCopyrights$ = this.store.select(FlatStates.CopyrightEntityState.allCurrentSelectedSlideCopyrights$);

  currentSelectedSlideHasSequenceControl$ = this.store.select(FlatStates.ImageEntityState.currentSelectedSlideHasSequenceControl$);
  currentSelectedSlideImageHasAnnotations$ = this.store.select(FlatStates.ImageEntityState.currentSelectedSlideImageHasAnnotations$);

  images$ = this.store.select(FlatStates.ImageEntityState.currentSelectedSlideValidImages$);
  defaultIndex$ = this.store.select(FlatStates.ImageEntityState.currentSelectedImagesDefaultIndex$);
  
  currentActiveEvents$ = this.store.select(FlatStates.MediaInteractionViewerState.currentActiveEvents$).pipe(distinctUntilChanged((prev, curr) => isEqual(prev, curr)));
  currentTime$ = this.store.select(FlatStates.MediaInteractionViewerState.currentTime$);

  //</editor-fold>

  //<editor-fold desc="Lazy Selectors">

  page$ = (id: string) => this.store.select(FlatStates.PageEntityState.entity$).pipe(map(filterFn => filterFn(id)));

  annotation = (id: string) => this.store.select(FlatStates.AnnotationEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  annotationLabel = (id: string) => this.store.select(FlatStates.AnnotationLabelEntityState.entity$).pipe(map(filterFn => filterFn(id)));

  slideUrlByNode$ = (slideNode: FlatModels.NodeEntityModels.SlideNode) => this.store.select(FlatStates.SlideViewerState.slideUrlByNode$).pipe(map(filterFn => filterFn(slideNode)));

  //</editor-fold>

  //<editor-fold desc="Snapshot Selectors">

  snapshot_areaGroupId = () => this.store.selectSnapshot(FlatStates.AnnotationGroupEntityState.areaGroupId$);
  snapshot_currentSelectedSlideGroup = () => this.store.selectSnapshot(FlatStates.AnnotationGroupEntityState.currentSelectedSlideGroup$);
  snapshot_currentSelectedSlideAnnotation = () => this.store.selectSnapshot(FlatStates.AnnotationEntityState.currentSelectedSlideAnnotation$);
  snapshot_imageIndex = () => this.store.selectSnapshot(FlatStates.ImageEntityState.imageIndex$);
  snapshot_eventModalById = (id: string) => this.store.selectSnapshot(FlatStates.MediaInteractionViewerState.eventModalsByIdMap$).get(id) || null;
  snapshot_currentSelectedSlidePage = () => this.store.selectSnapshot(FlatStates.PageEntityState.currentSelectedSlidePage$);

  //</editor-fold>

  //<editor-fold desc="Requests">

  requestGetSlideDataByPageId(pageId: string) {
    return this.store.dispatch(new FlatActions.SlideControlActions.GetSlideDataByPageId(pageId));
  }
  
  requestGetSlideDataBySlideId(slideId: number) {
    return this.store.dispatch(new FlatActions.SlideControlActions.GetSlideDataBySlideId(slideId));
  }

  requestSetLayerIndex(layerIndex: number) {
    const layerCount = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.currentSelectedSlideLayers$).length || 0;
    if (0 <= layerIndex && layerIndex <= (layerCount - 1)) {
      return this.store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layerIndex));
    }
    return;
  }

  requestSetImageIndex(imageIndex: number) {
    const imageCount = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideValidImages$).length || 0;
    if (0 <= imageIndex && imageIndex <= (imageCount - 1)) {
      return this.store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(imageIndex));
    }
    return;
  }

  requestSetCurrentTime(currentTime: number) {
    return this.store.dispatch(new FlatActions.MediaInteractionViewerActions.SetCurrentTime(currentTime));
  }

  requestAddEvents(events: FlatModels.MediaInteractionViewerModels.MediaInteractionEvent[]) {
    return this.store.dispatch(new FlatActions.MediaInteractionViewerActions.AddEvents(events));
  }

  requestClearEvents() {
    return this.store.dispatch(new FlatActions.MediaInteractionViewerActions.ClearEvents());
  }

  requestJumpToTime(time: number,
                    force?: FlatActions.MediaInteractionViewerActions.JumpToTime['force']) {
    return this.store.dispatch(new FlatActions.MediaInteractionViewerActions.JumpToTime(time, force));
  }

  requestOpenModal(targetId: string,
                   onClose?: FlatActions.MediaInteractionViewerActions.OpenModal['onClose']) {
    return this.store.dispatch(new FlatActions.MediaInteractionViewerActions.OpenModal(targetId, onClose));
  }

  requestTogglePlay(force?: FlatActions.MediaInteractionViewerActions.TogglePlay['force']) {
    return this.store.dispatch(new FlatActions.MediaInteractionViewerActions.TogglePlay(force));
  }

  requestSetGroupId(groupId: string | null) {
    return this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.SetGroupId(groupId));
  }

  requestSetAreaGroupId(areaGroupId: string | null) {
    return this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.SetAreaGroupId(areaGroupId));
  }

  requestSetPoiId(poiId: string) {
    return this.store.dispatch(new FlatActions.PointOfInterestEntityActions.SetPoiId(poiId));
  }

  requestSetAnnotationId(annotationId: string | null) {
    return this.store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(annotationId));
  }

  requestGoToSlideByIndex(eventTarget: EventTarget,
                          currentIndex: number,
                          selectedSlides: FlatModels.NodeEntityModels.SlideNode[],
                          selectedSlideNodes: FlatModels.NodeEntityModels.SlideNode[],
                          queryParams: Params) {
    // Get Index
    const target = eventTarget as HTMLInputElement;
    let index = Number.parseInt(target.value, 10);
    if (!isNaN(index)) {
      index -= 1;
      if (index >= 0 && index < selectedSlides.length) {
        const selectedSlideId = selectedSlides[index].id;
        const selectedSlideNode = selectedSlideNodes.find(sn => sn.id === selectedSlideId);
        if (selectedSlideNode) {
          return this.store.dispatch(new FlatActions.SlideViewerActions.GoToSlideByNode(selectedSlideNode, queryParams));
        } else {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            'notification.node_not_found',
            FlatModels.ErrorControlModels.ErrorLevel.enum.error,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatFacades.SlideViewerFacade.requestGoToSlideByIndex"
          );
        }
      }
    }

    // Reset Index Value
    target.value = (currentIndex + 1).toString();
    return false;
  }

  //</editor-fold>

}
